/* Menu button component */

import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';

import anim from 'Style/anim';
import border from 'Style/border';
import font from 'Style/font';
import size from 'Style/size';

import color from '../../../color';
import withMouseOver from '../higher_order_components/with_mouse_over';

import PopoverButton from './popover_button';

function getStyle() {
  const trigger = {
    container: {
      display: 'flex',
      alignItems: 'center',
      height: `${size.controlHeight}px`,
      cursor: 'pointer',
    },
    label: {
      fontFamily: font.family.sans,
      fontSize: font.size.body,
      color: color.grayXL,
    },
  };
  const option = {
    padding: '10px 18px',
    fontFamily: font.family.sans,
    fontSize: font.size.body,
    color: color.grayL,
    whiteSpace: 'nowrap' as const,
    outline: 'none',
    cursor: 'pointer',
    ...anim.std,
  };
  return {
    trigger: {
      arrow: {
        marginLeft: '6px',
        fontSize: font.size.extraSmall,
        color: color.grayM,
      },
      getContainerStyle(extraStyle?: React.CSSProperties) {
        return {
          ...trigger.container,
          justifyContent: 'center',
          width: '50px',
          ...extraStyle,
        };
      },
      getContainerStyleHover(hover: boolean, extraStyle?: React.CSSProperties) {
        return {
          ...trigger.container,
          backgroundColor: hover ? color.grayXXXS : 'transparent',
          padding: '0 20px',
          ...extraStyle,
        };
      },
      getLabelStyle(isElement: boolean) {
        return {
          ...trigger.label,
          marginLeft: isElement ? '6px' : 0,
        };
      },
    },
    option: {
      selectedLabel: {
        marginLeft: '10px',
      },
      unSelectedLabel: {
        marginLeft: '24px',
      },
      getStyle(hover: boolean) {
        return hover
          ? {
              ...option,
              color: color.emphasis,
            }
          : {
              ...option,
              color: color.grayL,
            };
      },
    },
    releaseNotesMarker: {
      display: 'inline-block',
      height: '8px',
      width: '8px',
      marginLeft: '25px',
      backgroundColor: color.emphasis,
      ...border().circle,
    },
    releaseNotesProfileMarker: {
      display: 'inline-block',
      height: '7px',
      width: '7px',
      position: 'absolute' as const,
      right: '8px',
      bottom: '18px',
      backgroundColor: color.emphasis,
      ...border().circle,
    },
  };
}

const defaultProps = {
  highlightOnHover: true,
};

type TRawTriggerProps = {
  containerId?: string;
  element: React.ReactNode;
  extraStyle?: React.CSSProperties;
  hasSeenAllReleaseNotes?: boolean;
  highlightOnHover?: boolean;
  hover: boolean;
  label?: string;
  menuType?: 'profile';
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  title?: string;
  withArrow?: boolean;
};

const RawTrigger: React.FC<TRawTriggerProps> = ({
  element,
  label,
  withArrow,
  title,
  hover,
  onMouseEnter,
  onMouseLeave,
  highlightOnHover,
  extraStyle,
  hasSeenAllReleaseNotes,
  menuType,
  containerId,
}) => {
  const containerStyle = highlightOnHover
    ? getStyle().trigger.getContainerStyleHover(hover, extraStyle)
    : getStyle().trigger.getContainerStyle(extraStyle);
  const labelStyle = label
    ? getStyle().trigger.getLabelStyle(!!element)
    : undefined;
  const labelElem = label ? <span style={labelStyle}>{label}</span> : null;
  const arrow = withArrow ? (
    <i className="icon-arrow-down" style={getStyle().trigger.arrow} />
  ) : null;
  return (
    <div
      title={title}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={containerStyle}
      id={containerId}
    >
      {element}
      {menuType === 'profile' && !hasSeenAllReleaseNotes && (
        <span style={getStyle().releaseNotesProfileMarker} />
      )}
      {labelElem}
      {arrow}
    </div>
  );
};

RawTrigger.defaultProps = defaultProps;

const Trigger = withMouseOver(RawTrigger);

type TRawOptionProps = {
  action: () => void;
  dataTest?: string;
  hasSeenAllReleaseNotes?: boolean;
  hover: boolean;
  label: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  selected?: string;
  title?: string;
  value: string;
};

const RawOption: React.FC<TRawOptionProps> = ({
  value,
  label,
  title,
  dataTest,
  action,
  selected,
  hover,
  onMouseEnter,
  onMouseLeave,
  hasSeenAllReleaseNotes,
}) => {
  const optionStyle = getStyle().option.getStyle(hover);
  let check;
  let labelStyle;
  if (selected) {
    const isSelected = value === selected;
    check = isSelected ? <i className="icon-ok" /> : null;
    labelStyle = isSelected
      ? getStyle().option.selectedLabel
      : getStyle().option.unSelectedLabel;
  } else {
    check = null;
  }
  const releaseNoteIcon = (
    <span>
      {value === 'release_notes' && !hasSeenAllReleaseNotes && (
        <span style={getStyle().releaseNotesMarker} />
      )}
    </span>
  );
  return (
    <div
      title={title}
      data-test={dataTest}
      onClick={action}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={optionStyle}
    >
      {check}
      <span style={labelStyle}>{label}</span>
      {releaseNoteIcon}
    </div>
  );
};

const Option = withMouseOver(RawOption);

type TMenuButtonProps = {
  containerId?: string;
  extraStyle?: React.CSSProperties;
  extraStylePop?: React.CSSProperties;
  hasSeenAllReleaseNotes?: boolean;
  highlightOnHover?: boolean;
  menuType?: TRawTriggerProps['menuType'];
  onOptionClick: (v: string) => void;
  options: {
    value: string;
    label: string;
    title?: string;
    dataTest?: string;
  }[];
  selected?: string;
  side: React.ComponentProps<typeof PopoverButton>['side'];
  triggerElement: React.ReactNode;
  triggerLabel?: string;
  triggerTitle?: string;
  withArrow?: boolean;
};

const MenuButton: React.FC<TMenuButtonProps> = ({
  triggerElement,
  triggerLabel,
  withArrow,
  triggerTitle,
  options,
  onOptionClick,
  selected,
  side,
  extraStyle,
  highlightOnHover,
  hasSeenAllReleaseNotes,
  menuType,
  containerId,
  extraStylePop,
}) => {
  const {t} = useTranslation();
  const popoverButtonRef = useRef<PopoverButton>(null);
  const handleOptionClick = (value: string) => {
    popoverButtonRef.current?.close();
    onOptionClick(value);
  };

  const trigger = (
    <Trigger
      element={triggerElement}
      label={triggerLabel}
      withArrow={withArrow}
      title={triggerTitle}
      highlightOnHover={highlightOnHover}
      extraStyle={extraStyle}
      hasSeenAllReleaseNotes={hasSeenAllReleaseNotes}
      menuType={menuType}
      containerId={containerId}
    />
  );
  const optionsList = options.map(({value, label, title, dataTest}) => (
    <Option
      key={`option_${value}`}
      value={value}
      label={t(label, {ns: ['account', 'project']})}
      title={title}
      dataTest={dataTest}
      action={() => handleOptionClick(value)}
      selected={selected}
      hasSeenAllReleaseNotes={hasSeenAllReleaseNotes}
    />
  ));
  const popover = <>{optionsList}</>;
  const popoverSide = side || 'right'; // default
  return (
    <PopoverButton
      ref={popoverButtonRef}
      trigger={trigger}
      popover={popover}
      side={popoverSide}
      extraStylePop={extraStylePop}
    />
  );
};

export default MenuButton;
